import type { PicSlotAdmin } from "@/components/admin-portal/appointment/form/pic-slot-selectors";
import type {
	GENDERS,
	PATIENT_CONDITIONS,
	PREFERRED_THERAPIST_GENDER,
} from "@/lib/constants";
import type { User } from "../auth";
import type { Admin } from "./admin";
import type { Location } from "./location";
import type { Package } from "./package";
import type { Patient, PatientMedicalRecord } from "./patient";
import type { Service } from "./service";
import type { Therapist } from "./therapist";

export enum AppointmentStatuses {
	cancelled = "CANCELLED",
	refunded = "REFUNDED",
	unscheduled = "UNSCHEDULED",
	scheduled = "SCHEDULED",
	on_hold = "ON HOLD",
	pending_therapist_assignment = "PENDING THERAPIST ASSIGNMENT",
	pending_patient_approval = "PENDING PATIENT APPROVAL",
	tentative = "TENTATIVE",
	pending_mitra_approval = "PENDING MITRA APPROVAL",
	pending_payment = "PENDING PAYMENT",
	paid = "PAID",
	completed = "COMPLETED",
}

export enum OrderPaymentStatuses {
	paid = "PAID",
	unpaid = "UNPAID",
}

type ProfileTypeMap = {
	admin: Admin;
	therapist: Therapist;
	patient: Patient;
	unknown: Record<any, any>;
};

export type StatusHistoryChangedBy = {
	[K in keyof ProfileTypeMap]: {
		profileType: K;
		profile: ProfileTypeMap[K];
	} & Pick<User, "id" | "email">;
}[keyof ProfileTypeMap];

export interface ChangeDetail<T = any> {
	old?: T | null;
	new?: T | null;
}

export interface TherapistChange {
	id?: string | null;
	name?: string | null;
}

export interface RescheduleMetadata {
	appointmentDatetime?: { old: string | null; new: string | null } | null;
	status?: { old: string | null; new: string | null } | null;
	visitNumber?: { old: number | null; new: number | null } | null;
	therapistId?: { old: string | null; new: string | null } | null;
	therapistName?: { old: string | null; new: string | null } | null;
}

// Written by Update Address; `source` and `submissionId` are plain values, not changes.
export interface AddressUpdateMetadata {
	source?: "active_address" | "manual";
	submissionId?: string;
	city?: ChangeDetail<string>;
	addressLine?: ChangeDetail<string>;
	postalCode?: ChangeDetail<string>;
	latitude?: ChangeDetail<number>;
	longitude?: ChangeDetail<number>;
	notes?: ChangeDetail<string>;
}

// One visit a package downgrade deleted, and which documents it was carrying at the time.
// Only the fact of each document, not its content — see the comment on #change_package in
// the appointments controller.
export interface RemovedVisitSnapshot {
	visitNumber?: number | null;
	status?: string | null;
	hadSoap?: boolean;
	hadEvidence?: boolean;
	hadMedicalRecord?: boolean;
}

// The order-level events (payment, package, voucher) and the bypass flags on a reschedule
// mix old/new pairs with plain facts — which checks were overridden, which settlement
// preset was picked, what a downgrade destroyed. One open shape rather than an interface
// per event: the timeline renders whatever shape each value has.
export type HistoryMetadataValue =
	| ChangeDetail<string | number | boolean>
	| RemovedVisitSnapshot[]
	| string[]
	| string
	| number
	| boolean
	| null;

export type HistoryMetadata = Record<string, HistoryMetadataValue | undefined>;

export interface StatusHistory {
	oldStatus: string;
	newStatus: string;
	reason: string;
	changedAt: string;
	changedBy: StatusHistoryChangedBy;
	eventType?: string | null;
	metadata?:
		| RescheduleMetadata
		| AddressUpdateMetadata
		| HistoryMetadata
		| null;
}

export interface AppointmentSoap {
	subject?: string;
	objective?: string;
	assessment?: string;
	planning?: string;
	additionalNotes?: string;
	isFinalVisit: boolean;
	initialPhysicalCondition?: string;
	therapyGoalEvaluation?: string;
	followUpTherapyPlan?: string;
	nextPhysiotherapyGoals?: string;
	therapyOutcomeSummary?: string;
	needsFollowUp?: boolean;
	recommendedVisits?: number;
	notes?: string;
	createdAt: string;
	updatedAt: string;
	therapistName?: string;
}

export interface AppointmentEvidencePhoto {
	objectKey: string;
	// Nullable in the database and null on real rows — the uploader does not always
	// classify a photo.
	photoType: string | null;
	fileName?: string;
	fileSize?: number;
	contentType?: string;
	createdAt: string;
}

export interface AppointmentEvidence {
	token: string;
	nonce: string;
	latitude?: number;
	longitude?: number;
	userAgent?: string;
	ipAddress?: string;
	notes?: string;
	createdAt: string;
	updatedAt: string;
	photos: AppointmentEvidencePhoto[];
}

export interface Appointment {
	id: string;
	therapistId: string | null;
	patientId: string;
	serviceId: number;
	packageId: number;
	locationId: number;
	order?: {
		id: string;
		registrationNumber: string;
		bookingDraftId: string | null;
		patientId: string;
		packageId: number;
		packageBasePrice: string;
		subtotal: string;
		discountType: string | null;
		discountValue: string;
		discountAmount: string;
		voucherCode: string | null;
		taxPercentage: string;
		taxAmount: string;
		totalAmount: string;
		paidAmount: string;
		remainingAmount: string;
		paymentStatus: string;
		invoiceNumber: string | null;
		invoiceUrl: string | null;
		invoiceDueDate: string;
		status: string;
		specialNotes: string | null;
		cancellationReason: string | null;
		cancelledAt: string | null;
		completedAt: string | null;
		createdAt: string;
		updatedAt: string;
		userId: string;
		voucherId: string | null;
		cancelledBy: string | null;
	};
	admins?: Admin[];
	// The AppointmentDraft this visit came from, shown and copyable in the detail panel.
	// Absent on the list payload (only fetch_selected_appointment resolves it) and null for
	// a visit made before Drafts existed or synced from the spreadsheet.
	draftId?: string | null;
	reconcilableOrderId?: string | null;
	// The KPI slots of the Draft this Order came from, for the Update PIC dialog. Null or
	// absent when DRAFT_PIC_KPI is off or when there is no Draft behind this appointment
	// (one made before Drafts existed, or synced from the spreadsheet) — the dialog then
	// falls back to editing the flat PIC list. See docs/adr/0005.
	draftPics?: {
		picL1: PicSlotAdmin | null;
		picL2: PicSlotAdmin | null;
		picL3: PicSlotAdmin | null;
		// Everyone on the Draft who holds no slot. Assembled server-side from #pic_roles so
		// the dialog and the Drafts page badges cannot disagree about who counts.
		supporting: PicSlotAdmin[];
	} | null;
	therapist?: Therapist;
	patient?: Patient;
	service?: Service;
	package?: Package;
	location?: Location;
	registrationNumber: string;
	status: keyof typeof AppointmentStatuses;
	appointmentDateTime: string;
	preferredDate: string | null;
	timePreference: "PAGI" | "SIANG" | "SORE" | "MALAM" | null;
	preferredTherapistGender: (typeof PREFERRED_THERAPIST_GENDER)[number];
	referralSource: string | null;
	otherReferralSource: string | null;
	fisiohomePartnerBooking: boolean;
	fisiohomePartnerName: string | null;
	otherFisiohomePartnerName: string | null;
	voucherCode: string | null;
	notes: string | null;
	voucherDiscount: string;
	formattedDiscount: string;
	totalPrice: string;
	formattedTotalPrice: string;
	invoiceNumber?: string | null;
	invoiceUrl?: string | null;
	startTime?: string;
	endTime?: string;
	patientMedicalRecord?: PatientMedicalRecord;
	visitAddress?: {
		id: number;
		appointmentId: string;
		locationId: number;
		latitude: number;
		longitude: number;
		addressLine: string;
		postalCode: string;
		notes: string;
		coordinates: {
			x: number;
			y: number;
		};
		createdAt: string;
		updatedAt: string;
		// The latest Update Address change; carried on the allVisits entries.
		changedAt?: string | null;
		changeSource?: "active_address" | "manual" | null;
		changedByName?: string | null;
	};
	// Assigned therapist's name; carried on the Update Address allVisits entries.
	therapistName?: string | null;
	visitNumber: number;
	appointmentReferenceId: string | null;
	initialVisit: boolean;
	visitProgress: string;
	nextVisitProgress: string;
	totalPackageVisits: number;
	seriesAppointments: Appointment[];
	allVisits?: Appointment[];
	statusHistories: StatusHistory[];
	soap?: AppointmentSoap;
	// The packages an admin may recommend for a next course of therapy, carried only by the
	// payload that opens the Update SOAP dialog.
	soapVisitOptions?: { name: string; numberOfVisit: number }[];
	evidence?: AppointmentEvidence;
	// Presence flags for the two documents above, carried by the list payloads that do not
	// serialize either in full. Absent on payloads that ask for neither.
	hasSoap?: boolean;
	hasEvidence?: boolean;
	// Completed with both of them filed: nothing may move this visit any more, its status
	// or its schedule. Computed server-side by Appointment#documents_locked?; only the
	// payloads that gate an action carry it.
	documentsLocked?: boolean;
	isPaid: boolean;
	isCompleted: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface AppointmentPayload {
	serviceId: string;
	packageId: string;
	locationId: string;
	therapistId: string | null;
	adminIds: string | null;
	queueCode: string | null;
	referenceAppointmentId: string | null;
	draftId?: string | null;
	patientContact: {
		contactName: string;
		contactPhone: string;
		email?: string;
		miitelLink?: string;
	};
	patientAddress: {
		locationId: string;
		latitude: number;
		longitude: number;
		postalCode: string;
		address: string;
		notes?: string;
	};
	patient: {
		name: string;
		gender: (typeof GENDERS)[number];
		dateOfBirth: Date;
		age?: number;
	};
	appointment: {
		patientIllnessOnsetDate?: string;
		patientComplaintDescription: string;
		patientCondition: (typeof PATIENT_CONDITIONS)[number];
		patientMedicalHistory?: string;

		// appointment scheduling
		appointmentDateTime: Date | null;
		preferredTherapistGender: (typeof PREFERRED_THERAPIST_GENDER)[number];

		//  additional settings
		referralSource?: string;
		otherReferralSource?: string;
		fisiohomePartnerBooking: boolean;
		fisiohomePartnerName?: string;
		otherFisiohomePartnerName?: string;
		voucherCode?: string;
		notes?: string;
		bypassSlotConflict?: boolean;
		mitraAssignmentReason?: string | null;

		// series visits
		seriesVisits?: {
			appointmentDateTime: Date | null;
			therapistId: string | null;
			visitNumber: number;
			mitraAssignmentReason?: string | null;
		}[];
	};
}
